import React, { useEffect, useMemo, useState } from "react";
import { Column, Row } from "react-table";
import EventLogApi from "../../api/EventLogApi";
import { EModuleType } from "../../Model/EModuleType";
import { EScheduleEntryType, EventLog } from "../../Model/EventLog";
import { EPlateErrorStatus } from "../../Model/Plate";
import { PlateAddress } from "../../Model/PlateAddress";
import { getPlateAddresses } from "../../redux/reducers/PlateAddressReducer";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import CommonTable from "../Common/CommonTable";
import Breadcrumb from "../Header/Breadcrumb";
import Header from "../Header/Header";
import LayoutContent from "../LayoutContent";
import PlateInformation from "../Plate/CommandPanelViews/PlateInformation";
import DateFilter from "./DateFilter";
import SchedulingNavSubMenu from "./SchedulingNavSubMenu";
import DateUtil from "../../Utility/DateUtil";
import CommandPanel from "../CommandPanel/CommandPanel";
import EmptyScheduleMessage from "./EmptyScheduleMessage";

type Props = {
  isEventLogChartShowed: boolean;
  setIsEventLogChartShowed: (show: boolean) => void;
  setEventLogTextExport: React.Dispatch<React.SetStateAction<string>>;
  style?: React.CSSProperties;
};

function getEventLogRowStyle(
  value: EventLog,
  isSelected: boolean
): React.CSSProperties {
  let style: React.CSSProperties = {};
  if (isSelected) {
    style.background = "#F7A246";
    style.color = "white";
    return style;
  }

  return style;
}

function getPlateAddressByEventLog(
  plateAddresses: PlateAddress[],
  eventLog: EventLog
): PlateAddress {
  const filledPlateAddressed = plateAddresses.filter((p) => p.usedByPlate);
  const plateIdPlateAddresses = filledPlateAddressed.filter(
    (p) => p.usedByPlate?.plateId === eventLog.plateId
  );
  if (plateIdPlateAddresses.length != 0) {
    return plateIdPlateAddresses[0];
  }

  return {
    id: 0,
    moduleIdentifier: "",
    moduleName: "",
    moduleType: EModuleType.CHAMBER,
    hotelNo: 0,
    slotNo: 0,
    usedByPlate: {
      id: 0,
      plateId: eventLog.plateId,
      plateType: {
        id: 0,
        plateTypeName: eventLog.plateTypeName,
        numberOfColumn: 0,
        numberOfRow: 0,
        footprint: 0,
        slotHeight: 0,
        numberOfWell: 0,
        numberOfDropPerWell: 0,
        pitchX: 0,
        pitchY: 0,
        wellCoordinates: [],
        dropCoordinates: [],
        dropCalibrationPoints: [],
        wellLimit: {
          min: { x: 0, y: 0, z: 0 },
          max: { x: 0, y: 0, z: 0 },
        },
      },
      barcode: "-",
      projectName: eventLog.projectName,
      experimentName: eventLog.experimentName,
      setupTemp: 0,
      incubationTemp: 0,
      plateNumber: eventLog.plateNumber,
      userName: eventLog.userName,
      userEmail: "-",
      errorStatus: EPlateErrorStatus.OK,
      imagingStatus: 0,
      isReferencePlate: false,
      isExpired: false,
      dateDispensed: "-",
      isQueuedForTransfer: false,
      errorStatusMessages: [],
    },
  };
}

export default function ImagingHistory() {
  const [dateRange, setDateRange] = useState<{ minDate: Date; maxDate: Date }>({
    minDate: new Date(),
    maxDate: new Date(),
  });
  const [eventLogs, setEventLogs] = useState<EventLog[]>([]);
  const [selectedEventLog, setSelectedEventLog] = useState<EventLog[]>([]);

  const plateAddresses = useTypedSelector((state) => getPlateAddresses(state));

  const sortByTimeFn = (
    rowA: Row<EventLog>,
    rowB: Row<EventLog>,
    columnId: string,
    desc?: boolean
  ) => {
    const dateA = new Date(rowA.original.createdDate);
    const dateB = new Date(rowB.original.createdDate);

    if (dateA === dateA) {
      return 0;
    } else if (dateA > dateB) {
      return -1;
    } else {
      return 1;
    }
  };

  const getTableColumnConfig: Column<EventLog>[] = useMemo(
    () => [
      {
        id: "date",
        Header: "Date",
        accessor: (p) => {
          return DateUtil.toUsFormatString(new Date(p.scheduled));
        },
        sortType: sortByTimeFn,
        Filter: (
          <DateFilter dateRange={dateRange} setDateRange={setDateRange} />
        ),
      },
      {
        id: "projectName",
        Header: "Project Name",
        accessor: (p) => {
          return p.projectName;
        },
      },
      {
        id: "experimentName",
        Header: "Experiment Name",
        accessor: (p) => {
          return p.experimentName;
        },
      },
      {
        id: "plateNumber",
        Header: "Plate Number",
        accessor: (p) => {
          return p.plateNumber;
        },
        width: "5%",
      },
      {
        id: "userName",
        Header: "User Name",
        accessor: (p) => {
          return p.userName;
        },
        width: "10%",
      },
      {
        id: "plateType",
        Header: "Plate Type",
        accessor: (p) => {
          return p.plateTypeName;
        },
      },
      {
        id: "plateID",
        Header: "Plate ID",
        accessor: (p) => {
          return p.plateId;
        },
        width: "7%",
      },
      {
        id: "imagingMode",
        Header: "Imaging Mode",
        accessor: (p) => {
          return EScheduleEntryType[p.scheduleEntryType];
        },
        width: "8%",
      },
      {
        id: "completedImaging",
        Header: "Completed Imaging",
        accessor: (p) => {
          return DateUtil.toUsFormatString(new Date(p.lastUpdatedDate));
        },
      },
    ],
    []
  );

  useEffect(() => {
    EventLogApi.getImagingBetweenEventLog(
      dateRange.minDate,
      dateRange.maxDate
    ).then((data) => {
      setEventLogs(data);
    });
  }, [dateRange]);

  return (
    <LayoutContent>
      <LayoutContent.Header>
        <Header>
          <Breadcrumb
            items={[{ title: "History", to: "/schedule", disabled: false }]}
          />
        </Header>
      </LayoutContent.Header>
      <LayoutContent.Content>
        <CommonTable<EventLog>
          data={eventLogs}
          columns={getTableColumnConfig}
          getRowStyle={getEventLogRowStyle}
          pageLineSize={11}
          setSelectedRows={setSelectedEventLog}
          enableMultiSelect={false}
          sortingRules={[
            {
              id: "time",
            },
          ]}
        />
      </LayoutContent.Content>
      <LayoutContent.SubMenu>
        <SchedulingNavSubMenu />
      </LayoutContent.SubMenu>
      <LayoutContent.CommandPanel>
        {selectedEventLog.length === 1 ? (
          <PlateInformation
            plateAddress={getPlateAddressByEventLog(
              plateAddresses,
              selectedEventLog?.[0]
            )}
            backBtn={() => setSelectedEventLog([])}
          />
        ) : (
          <CommandPanel>
            <CommandPanel.TitleHeader title="Schedule" />
            <EmptyScheduleMessage style={{ marginTop: 40 }} />
          </CommandPanel>
        )}
      </LayoutContent.CommandPanel>
    </LayoutContent>
  );
}
